import AutorenewIcon from '@mui/icons-material/Autorenew'
import DeleteIcon from '@mui/icons-material/Delete'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonIcon from '@mui/icons-material/Person'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import { useNavigate } from 'react-router-dom'
import CircularLoading from '../../../../../../../components/CircularProgress'
import Pagination from '../../../../../../../components/Pagination'
import { Tag } from '../../../../../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import PageUrls from '../../../../../../../constants/page-urls'
import { axiosInstance } from '../../../../../../../hooks/useAxios'
import {
  ILead,
  ILeadAssignTo,
  ILeadCustomer,
} from '../../../../../../../types/lead'
import ConfirmCancelModal from './ConfirmCancelModal'
import { IFilterMapped } from './LeadListFilter'

interface ILeadListTableProps {
  selectedData: ILead[]
  setSelectedData: (leads?: ILead[]) => void
  isLoading: boolean
  data?: ILead[]
  onUpdatePipelineListSuccess: () => void
  filter: IFilterMapped
  onSearch: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
  onSendLead: (lead: ILead) => void
  onMoveLead: (lead: ILead) => void
}

const LeadListTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  onUpdatePipelineListSuccess,
  filter,
  onSearch,
  handlePageChange,
  total,
  onSendLead,
  onMoveLead,
}: ILeadListTableProps) => {
  const navigate = useNavigate()
  const [isConfirmModal, setIsConfirmModal] = useState<boolean>(false)
  const [selectedLead, setSelectedLead] = useState<ILead>()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openMenu, setOpenMenu] = React.useState<string>('')
  const open = Boolean(anchorEl)
  const onHandleCancelLead = async (lead: ILead, note: string) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(`/lead/cancel-leads`, {
        data: {
          objectIds: [lead.objectId!],
          note,
        },
      })
      message.success(`ลบข้อมูลสำเร็จ`)
      onUpdatePipelineListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }
  const handleConfirmCancelLead = async (lead: ILead) => {
    setSelectedLead(lead)
    setIsConfirmModal(true)
  }

  const columns: TableColumnsType<ILead & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ชื่อ-นามสกุล',
        dataIndex: 'name',
        key: 'name',
        align: 'center' as const,
        render: (name: string, lead: ILead) => {
          return (
            <Box
              sx={{
                minWidth: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Box ml={1}>
                <Typography variant="subtitle1">{name}</Typography>
                <Typography
                  variant="subtitle1"
                  fontSize={14}
                  color="text.secondary"
                >
                  ชื่อลูกค้า: {lead.customer?.name}
                </Typography>
                {lead?.distribution && (
                  <Typography
                    variant="subtitle2"
                    fontSize={14}
                    color="text.secondary"
                  >
                    ช่องทางการขาย: {lead?.distribution?.name}
                  </Typography>
                )}

                {lead?.tags?.length > 0 && (
                  <Box display={'flex'} flexWrap={'wrap'} gap={1} mt={1}>
                    {lead.tags.map((tag: string) => (
                      <Tag key={tag} color={'success'}>
                        {tag}
                      </Tag>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )
        },
      },
      {
        title: 'ที่อยู่',
        dataIndex: 'customer',
        key: 'customer',
        align: 'center' as const,
        render: (customer: ILeadCustomer) => {
          return (
            <Box sx={{ minWidth: 170, textAlign: 'left' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {customer?.address} {customer?.subDistrict} ตำบล/แขวง อำเภอ/เขต{' '}
                {customer?.province} {customer?.zipcode}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'หมายเลขโทรศัพท์',
        dataIndex: 'customer',
        key: 'customer',
        width: 120,
        align: 'center' as const,
        render: (customer: ILeadCustomer) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {customer?.phone}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ผู้ดูแล',
        dataIndex: 'assignTo',
        key: 'assignTo',
        width: 120,
        align: 'center' as const,
        render: (assignTo: ILeadAssignTo) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {assignTo?.name}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'PIPELINE',
        dataIndex: 'pipelineName',
        key: 'pipelineName',
        width: 120,
        align: 'center' as const,
        render: (pipelineName: string) => {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <Tag color={'warning'}>
                <Typography variant="subtitle2">{pipelineName}</Typography>
              </Tag>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'activePipelineItemName',
        key: 'activePipelineItemName',
        width: 120,
        align: 'center' as const,
        render: (activePipelineItemName: string, lead: ILead) => {
          return (
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={1}
              sx={{ textAlign: 'center' }}
            >
              <Tag color={'info'}>{activePipelineItemName}</Tag>
              {lead.isFinished && <Tag color={'error'}>สิ้นสุด lead แล้ว</Tag>}
            </Box>
          )
        },
      },
      {
        title: 'วันหมดอายุ',
        dataIndex: 'expiredAt',
        key: 'expiredAt',
        width: 160,
        align: 'center' as const,
        render: (expiredAt: string) => {
          return (
            <Box>
              <Tag
                color={dayjs().isAfter(dayjs(expiredAt)) ? 'error' : 'success'}
              >
                <Typography variant="subtitle2">
                  {dayjs().isAfter(dayjs(expiredAt)) ? 'หมดอายุ' : 'ใช้งานได้'}
                </Typography>
              </Tag>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(expiredAt).format(ONLY_DATE_FORMAT)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(expiredAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สร้างเมื่อ',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'center' as const,
        render: (createdAt: string) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Box>
          )
        },
      },
      {
        dataIndex: '',
        key: 'action',
        width: 80,
        align: 'center' as const,
        render: (_: undefined, lead: ILead) => {
          return (
            <Box>
              <IconButton
                id={`button-${lead.objectId}`}
                aria-controls={`menu-${lead.objectId}`}
                onClick={(event) => {
                  setOpenMenu(lead.objectId!)
                  setAnchorEl(event.currentTarget)
                }}
              >
                <MoreHorizIcon />
              </IconButton>
              <Menu
                id={`menu-${lead.objectId}`}
                anchorEl={anchorEl}
                open={openMenu === lead.objectId}
                onClose={() => {
                  setAnchorEl(null)
                  setOpenMenu('')
                }}
              >
                <MenuItem
                  component={'a'}
                  href={`${PageUrls.CRM.Lead.Detail}/${lead.objectId}`}
                >
                  <PersonIcon />
                  <ListItemText sx={{ marginLeft: 1 }}>
                    ดูรายละเอียดทะเบียนลูกค้า
                  </ListItemText>
                </MenuItem>
                {!lead.isFinished && (
                  <>
                    <MenuItem onClick={() => onMoveLead(lead)}>
                      <AutorenewIcon />
                      <ListItemText sx={{ marginLeft: 1 }}>
                        ย้าย Pipline
                      </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleConfirmCancelLead(lead)}>
                      <DeleteIcon />
                      <ListItemText sx={{ marginLeft: 1 }}>
                        เลิกติดตาม
                      </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => onSendLead(lead)}>
                      <SendIcon />
                      <ListItemText sx={{ marginLeft: 1 }}>
                        ส่งต่อ Lead
                      </ListItemText>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </Box>
          )
        },
      },
    ]

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const leadObjects = data?.reduce<{ [key: string]: ILead }>((acc, cur) => {
      acc[cur?.objectId!] = cur
      return acc
    }, {})
    const leads = keys.map((key: string) => {
      return leadObjects?.[key]
    })
    setSelectedData(leads as ILead[])
  }

  const rowSelection = {
    selectedRowKeys: selectedData.map((lead: ILead) => lead.objectId!),
    onChange: onSelectChange,
    // getCheckboxProps: (record: ILead) => ({
    //   style: record.isFinished ? { display: 'none' } : {},
    // }),
  }

  return (
    <>
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <ConfirmCancelModal
        open={isConfirmModal}
        onClose={() => setIsConfirmModal(false)}
        onConfirm={(note) => onHandleCancelLead(selectedLead!, note)}
        title={'ยืนยันการยกเลิกการติดตาม'}
        message={'คุณต้องการยกเลิกการติดตาม Lead ที่เลือกหรือไม่?'}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((lead: ILead, index: number) => {
          return {
            ...lead,
            key: lead.objectId!,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default LeadListTable
